import {
  Button,
  Form,
  Input,
  Modal,
  Space,
  Typography,
  Upload,
  message,
} from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

import type CommunityGroups from '@/types/database/communityGroups';

import styles from './index.module.scss';

interface AddAndEditCommunityModalProps {
  formType: 'add' | 'edit';
  updateCommunityGroups: () => void;
  record?: CommunityGroups;
}

const { Item, useForm } = Form;
const { Text } = Typography;

const AddAndEditCommunityModal = ({
  formType,
  updateCommunityGroups,
  record,
}: AddAndEditCommunityModalProps) => {
  const [form] = useForm();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (form && record) {
      form.setFieldsValue(record);
    }
  }, [form, record]);

  const handleEditButtonClick = () => {
    form.setFieldsValue(record);
    setIsOpenModal(true);
  };

  const handleFormSubmit = async (
    formValues: Pick<CommunityGroups, 'name' | 'address'>
  ) => {
    const { name, address } = formValues;
    const formData = new FormData();
    formData.append('name', name);
    formData.append('address', address);
    if (fileList.length > 0) {
      formData.append('file', fileList[0] as RcFile);
    }

    try {
      if (formType === 'add') {
        await axios({
          method: 'POST',
          url: '/ezcon/api/communityGroupsAdd',
          data: formData,
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
        });
      } else {
        const communityId = record?._id;
        if (communityId) {
          formData.append('_id', communityId);
        }
        await axios({
          method: 'PUT',
          url: '/ezcon/api/communityGroupsEdit',
          data: formData,
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
        });
      }
      updateCommunityGroups();
      setIsOpenModal(false);
      message.success(
        formType === 'add' ? '新增社區群組成功!' : '編輯社區群組成功!'
      );
    } catch (error) {
      message.error(
        formType === 'add' ? '新增社區群組失敗!' : '編輯社區群組失敗!'
      );
    }
  };

  return (
    <>
      <Button onClick={handleEditButtonClick}>
        {formType === 'add' ? '新增社區群組' : '編輯'}
      </Button>
      <Modal
        open={isOpenModal}
        footer={null}
        title={formType === 'add' ? '新增社區群組' : '編輯社區群組'}
        centered
        destroyOnClose
        onCancel={() => setIsOpenModal(false)}
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          onFinish={handleFormSubmit}
        >
          <Item
            label={<Text type="secondary">社區名稱</Text>}
            name="name"
            rules={[{ required: true, message: '請輸入社區名稱' }]}
          >
            <Input placeholder="請輸入社區名稱" />
          </Item>
          <Item
            label={<Text type="secondary">社區地址</Text>}
            name="address"
            rules={[{ required: true, message: '請輸入社區地址' }]}
          >
            <Input placeholder="請輸入社區地址" />
          </Item>
          <Item
            label={<Text type="secondary">社區戶號</Text>}
            name="file"
            valuePropName="fileList"
            getValueFromEvent={({ fileList }) => {
              return fileList;
            }}
          >
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setFileList([file]);
                return false;
              }}
              fileList={fileList}
            >
              <Button>匯入excel</Button>
            </Upload>
          </Item>
          <Item>
            <Space className={styles.footerButtonsContainer}>
              <Button onClick={() => setIsOpenModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddAndEditCommunityModal;
